import { OrdersRepository } from '@lib/api/repository/orders/orders.repository';
import { OrderUpdateInput, SellerCancellationInput } from '@lib/types';
import { AdminPageFilters } from '@lib/types/admin-page';

export default class OrdersService {
  getAdminStatistics(year: number, signal?: AbortSignal) {
    return this.repository.getAdminStatistics(year, signal);
  }

  listAdminPage(filters: AdminPageFilters, signal?: AbortSignal) {
    return this.repository.listAdminPage(filters, signal);
  }

  constructor(private repository: OrdersRepository) {}

  listPurchases(signal?: AbortSignal) {
    return this.repository.listPurchases(signal);
  }

  listSales(signal?: AbortSignal) {
    return this.repository.listSales(signal);
  }

  countPendingShipments(signal?: AbortSignal) {
    return this.repository.countPendingShipments(signal);
  }

  listAdmin(signal?: AbortSignal) {
    return this.repository.listAdmin(signal);
  }

  getDisputeMetrics(signal?: AbortSignal) {
    return this.repository.getDisputeMetrics(signal);
  }

  listNotifications(signal?: AbortSignal) {
    return this.repository.listNotifications(signal);
  }

  markAllNotificationsRead() {
    return this.repository.markAllNotificationsRead();
  }

  markNotificationRead(id: string) {
    return this.repository.markNotificationRead(id);
  }

  findPurchase(id: string, signal?: AbortSignal) {
    return this.repository.findPurchase(id, signal);
  }

  findSale(id: string, signal?: AbortSignal) {
    return this.repository.findSale(id, signal);
  }

  findAdmin(id: string, signal?: AbortSignal) {
    return this.repository.findAdmin(id, signal);
  }

  cancelBySeller(id: string, input: SellerCancellationInput) {
    return this.repository.cancelBySeller(id, input);
  }

  update(id: string, input: OrderUpdateInput) {
    return this.repository.update(id, input);
  }

  updateAdmin(id: string, input: OrderUpdateInput) {
    return this.repository.updateAdmin(id, input);
  }

  indicateDelivery(id: string, file: File) {
    return this.repository.indicateDelivery(id, file);
  }

  downloadDeliveryProof(id: string) {
    return this.repository.downloadDeliveryProof(id);
  }

  confirmReceived(id: string) {
    return this.repository.confirmReceived(id);
  }

  justifyNonShipment(id: string, justification: string) {
    return this.repository.justifyNonShipment(id, justification);
  }

  openDispute(
    id: string,
    reason: string,
    description: string,
    requestedOutcome: 'refund' | 'fulfillment',
  ) {
    return this.repository.openDispute(
      id,
      reason,
      description,
      requestedOutcome,
    );
  }

  respondToDispute(id: string, response: string, proposal?: string) {
    return this.repository.respondToDispute(id, response, proposal);
  }

  decideDisputeProposal(id: string, accept: boolean) {
    return this.repository.decideDisputeProposal(id, accept);
  }

  claimDispute(id: string) {
    return this.repository.claimDispute(id);
  }

  addDisputeInternalNote(id: string, note: string) {
    return this.repository.addDisputeInternalNote(id, note);
  }

  requestDisputeInfo(id: string, target: 'buyer' | 'seller', note: string) {
    return this.repository.requestDisputeInfo(id, target, note);
  }

  resolveDispute(
    id: string,
    resolution: 'buyer' | 'seller' | 'return',
    note: string,
  ) {
    return this.repository.resolveDispute(id, resolution, note);
  }

  markReturnShipped(id: string, trackingCode: string) {
    return this.repository.markReturnShipped(id, trackingCode);
  }

  retryRefund(id: string) {
    return this.repository.retryRefund(id);
  }

  confirmPhysicalReturn(id: string) {
    return this.repository.confirmPhysicalReturn(id);
  }

  listDisputeEvidence(id: string, signal?: AbortSignal) {
    return this.repository.listDisputeEvidence(id, signal);
  }

  uploadDisputeEvidence(id: string, file: File) {
    return this.repository.uploadDisputeEvidence(id, file);
  }

  downloadDisputeEvidence(
    id: string,
    evidenceId: string,
    signal?: AbortSignal,
  ) {
    return this.repository.downloadDisputeEvidence(id, evidenceId, signal);
  }

  listComments(id: string, signal?: AbortSignal) {
    return this.repository.listComments(id, signal);
  }

  addComment(id: string, message: string, image?: File) {
    return this.repository.addComment(id, message, image);
  }

  downloadCommentImage(id: string, commentId: string, signal?: AbortSignal) {
    return this.repository.downloadCommentImage(id, commentId, signal);
  }

  createReview(id: string, rating: number, comment: string) {
    return this.repository.createReview(id, rating, comment);
  }

  simulateSandboxPayment(id: string) {
    return this.repository.simulateSandboxPayment(id);
  }
}
